import { useEffect, useState } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import ScrollToTop from "react-scroll-to-top";
import axiosInstance from './axiosInstance';
import "./App.css";
import "bootstrap/dist/css/bootstrap.css";

import Navigation from "./components/Navigation/Navigation";
import Sidebar from "./components/Sidebar/Sidebar";
import Homepage from "./components/Homepage/Homepage";
import Channels from "./components/Channels/Channels";
import RacesEvents from "./components/RacesEvents/RacesEvents";
import SingleChannel from "./components/SingleChannel/SingleChannel";
import VideoPage from "./components/VideoPage/VideoPage";
import UploadVideo from "./components/UploadVideo/UploadVideo";
import History from "./components/History/History";
import Categories from "./components/Categories/Categories";
import Upload from "./components/Upload/Upload";
import Blog from "./components/Blog/Blog";
import BlogDetails from "./components/Blog/BlogDetails";
import Blank from "./components/Blank/Blank";
import FourZeroFour from "./components/FourZeroFour/FourZeroFour";
import Contact from "./components/Contact/Contact";
import Subscriptions from "./components/Subscriptions/Subscriptions";
import Auth from "./components/Auth/Auth";
import Settings from "./components/Settings/Settings";
import Account from "./components/Account/Account";
import ThemeProvider from "./components/ThemeProvider";
import AddCar from "./components/AddCar/AddCar";
import DownLoadApp from "./components/DownLoadApp/DownLoadApp";
import MatchCar from "./components/MatchCar/MatchCar";
import MemberList from "./components/MemberList/MemberList";
import TrackData from "./components/TrackData/TrackData";
import Track from "./components/Track/Track";
import EditTrack from "./components/EditTrack/edit-track";
import EditConfig from "./components/EditConfig/edit-config";
import EditGeoFences from "./components/EditGeoFences/edit-geo-fence";
import AddTrack from "./components/AddTrack/AddTrack";
import SingleTrack from "./components/SingleTrack/SingleTrack";
import SingleGeoFences from "./components/SingleGeoFences/SingleGeoFences";
import SingleConfiguration from "./components/SingleConfiguration/SingleConfiguration";
import UpdateTrack from './components/UpdateTrack/UpdateTrack';
import Configurations from "./components/Configurations/Configurations";
import AddConfigurations from "./components/AddConfigurations/AddConfigurations";
import GeoFences from "./components/GeoFences/GeoFences";
import AddGeoFences from "./components/AddGeoFences/AddGeoFences";
import { CustomScrollToTop } from "./components/Atomics/ScrollToTop/ScrollToTop";

// Function to check if the token is still valid
const checkTokenValidity = async () => {
    try {
        const response = await axiosInstance.get('/users/currentUser', {
            headers: { 'Authorization': localStorage.getItem('token') }
        });
        return response.data.success; // Token is valid
    } catch (error) {
        if (error.response && error.response.data.error.message.includes('jwt expired')) {
            localStorage.removeItem('token'); // Remove expired token
        }
        return false; // Token is not valid or expired
    }
};

// Helper function to protect routes
function PrivateRoute({ children, ...rest }) {
    const [isAuthenticated, setIsAuthenticated] = useState(null);

    useEffect(() => {
        const verifyToken = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                const valid = await checkTokenValidity();
                setIsAuthenticated(valid);
            } else {
                setIsAuthenticated(false); // No token found, user is not authenticated
            }
        };

        verifyToken();
    }, []);

    if (isAuthenticated === null) {
        // Show a loading state while checking token validity
        return <div>Loading...</div>;
    }

    return (
        <Route
            {...rest}
            render={({ location }) =>
                isAuthenticated ? (
                    children
                ) : (
                    <Redirect
                        to={{
                            pathname: "/auth/login",
                            state: { from: location }
                        }}
                    />
                )
            }
        />
    );
}

function App() {
    const [showMenu, setShowMenu] = useState(false);

    useEffect(() => {
        if (showMenu) {
            document.body.classList.add("sidebar-toggled");
        } else {
            document.body.classList.remove("sidebar-toggled");
        }
    }, [showMenu]);

    return (
        <ThemeProvider>
            <Router>
                <Switch>
                    {/* Public Route */}
                    <Route path="/auth" component={Auth} />

                    {/* Private Routes */}
                    <PrivateRoute path="/">
                        <Content showMenu={showMenu} setShowMenu={setShowMenu} />
                    </PrivateRoute>

                    {/* 404 Page */}
                    <Route path="*" component={FourZeroFour} />
                </Switch>
            </Router>
        </ThemeProvider>
    );
}

function Content(props) {
    return (
        <>
            <div className="homepage-wrapper">
                <Navigation props={props} />
                <div id="wrapper">
                    <Sidebar props={props} />

                    <Switch>
                        <Route exact path="/" component={DownLoadApp} />
                        <Route exact path="/channels" component={Channels} />
                        <Route exact path="/races-events" component={RacesEvents} />
                        <Route exact path="/single-channel" component={SingleChannel} />
                        <Route exact path="/video-page" component={VideoPage} />
                        <Route exact path="/upload-video" component={UploadVideo} />
                        <Route exact path="/track" component={Track} />
                        <Route exact path="/members-list" component={MemberList} />
                        <Route exact path="/track-data" component={TrackData} />
                        <Route exact path="/add-track" component={AddTrack} />
                        <Route exact path="/download-app" component={DownLoadApp} />
                        <Route exact path="/configurations" component={Configurations} />
                        <Route exact path="/add-configurations" component={AddConfigurations} />
                        <Route exact path="/geo-fences" component={GeoFences} />
                        <Route exact path="/add-geo-fences" component={AddGeoFences} />
                        <Route path="/track/:id" component={SingleTrack} />
                        <Route path="/edit-track/:id" component={EditTrack} />
                        <Route path="/edit-config/:id" component={EditConfig} />
                        <Route path="/edit-geo-fence/:id" component={EditGeoFences} />
                        <Route path="/config/:id" component={SingleConfiguration} />
                        <Route path="/geofence/:id" component={SingleGeoFences} />
                        <Route exact path="/update-track/:id" component={UpdateTrack} />
                        <Route exact path="/add-car" component={AddCar} />
                        <Route exact path="/match-car" component={MatchCar} />
                        <Route exact path="/history" component={History} />
                        <Route exact path="/categories" component={Categories} />
                        <Route exact path="/upload" component={Upload} />
                        <Route exact path="/blog" component={Blog} />
                        <Route exact path="/blog-details" component={BlogDetails} />
                        <Route exact path="/blank" component={Blank} />
                        <Route exact path="/contact-us" component={Contact} />
                        <Route exact path="/subscriptions" component={Subscriptions} />
                        <Route exact path="/settings" component={Settings} />
                        <Route exact path="/account" component={Account} />
                        <Route path="*" component={FourZeroFour} />
                    </Switch>
                </div>
                <ScrollToTop
                    smooth
                    component={<CustomScrollToTop />}
                    className="scroll-to-top outline-0"
                    color="white"
                />
            </div>
        </>
    );
}

export default App;
